import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export interface NavigationState {
  workflowId?: number;
  workflowName?: string;
  executionId?: number;
  executionName?: string;
  currentRoute?: string;
  breadcrumbPath?: BreadcrumbItem[];
}

export interface BreadcrumbItem {
  label?: string;
  routerLink?: string;
  icon?: string;
  disabled?: boolean;
}

export interface BreadcrumbParams {
  executionId?: number;
  executionName?: string;
  workflowId?: number;
  workflowName?: string;
  connectionName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbNavigationService {
  private readonly translate = inject(TranslateService);
  private navigationState = signal<NavigationState>({});
  private navigationSubject = new BehaviorSubject<NavigationState>({});
  private readonly languageVersion = signal(0);

  public navigation$ = this.navigationSubject.asObservable();

  constructor() {
    this.translate.onLangChange.subscribe(() => {
      this.languageVersion.update((v) => v + 1);
    });
  }

  getCurrentState(): NavigationState {
    return this.navigationState();
  }

  updateNavigationState(state: Partial<NavigationState>): void {
    const currentState = this.navigationState();
    const newState = { ...currentState, ...state };
    this.navigationState.set(newState);
    this.navigationSubject.next(newState);
  }

  generateBreadcrumbs(route: string, params?: BreadcrumbParams): BreadcrumbItem[] {
    this.languageVersion();
    const state = this.navigationState();

    switch (route) {
      // Monitoring routes
      case 'monitoring':
        return [this.home(), { label: this.t('breadcrumbs.monitoring'), disabled: true }];

      case 'workflow-executions':
        return [
          this.home(),
          { label: this.t('breadcrumbs.monitoring'), routerLink: '/monitoreo' },
          {
            label: state.workflowName || this.t('breadcrumbs.workflow'),
            routerLink: '/monitoreo',
            disabled: true,
          },
          { label: this.t('breadcrumbs.executions'), disabled: true },
        ];

      case 'execution-history': {
        const executionId = params?.executionId || state.executionId;
        const executionName = params?.executionName || state.executionName;
        const workflowId = params?.workflowId || state.workflowId;
        const workflowName = params?.workflowName || state.workflowName;

        return [
          this.home(),
          { label: this.t('breadcrumbs.monitoring'), routerLink: '/monitoreo' },
          {
            label: workflowName || this.t('breadcrumbs.workflowWithId', { id: workflowId }),
            routerLink: `/monitoreo/workflow/${workflowId}`,
          },
          {
            label: executionName || this.t('breadcrumbs.executionWithId', { id: executionId }),
            routerLink: `/monitoreo/workflow/${workflowId}`,
            disabled: true,
          },
          {
            label: this.t('breadcrumbs.historyId', { id: executionId }),
            disabled: true,
          },
        ];
      }

      // Users routes
      case 'users':
        return [this.home(), { label: this.t('breadcrumbs.users'), disabled: true }];

      // Connections routes
      case 'connections':
        return [this.home(), { label: this.t('breadcrumbs.connections'), disabled: true }];

      case 'connection-details':
        return [
          this.home(),
          { label: this.t('breadcrumbs.connections'), routerLink: '/connections' },
          { label: params?.connectionName || this.t('breadcrumbs.details'), disabled: true },
        ];

      // Homologations
      case 'homologations':
        return [this.home(), { label: this.t('breadcrumbs.homologations'), disabled: true }];

      case 'configuracion-flujo':
        return [this.home(), { label: this.t('breadcrumbs.flowConfiguration'), disabled: true }];

      case 'configuracion-flujo-works':
        return [
          this.home(),
          { label: this.t('breadcrumbs.flowConfiguration'), routerLink: '/configuracion-flujo' },
          { label: this.t('breadcrumbs.works'), disabled: true },
        ];

      case 'credit-note':
        return [
          { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
          { label: 'Notas de Crédito', disabled: true },
        ];

      case 'configuracion-flujo-mapeo':
        return [
          this.home(),
          { label: this.t('breadcrumbs.flowConfiguration'), routerLink: '/configuracion-flujo' },
          { label: this.t('breadcrumbs.mapping'), disabled: true },
        ];

      case 'configuracion-flujo-homologaciones':
        return [
          this.home(),
          { label: this.t('breadcrumbs.flowConfiguration'), routerLink: '/configuracion-flujo' },
          { label: this.t('breadcrumbs.homologations'), disabled: true },
        ];

      case 'dashboard':
        return [{ ...this.home(), disabled: true }];
      case 'dashboard-lambdas':
        return [this.home(), { label: this.t('breadcrumbs.dashboardLambdas'), disabled: true }];

      case 'providers':
        return [this.home(), { label: this.t('breadcrumbs.providers'), disabled: true }];

      case 'roles':
        return [this.home(), { label: this.t('breadcrumbs.rolesAndPermissions'), disabled: true }];

      case 'pre-register':
        return [this.home(), { label: this.t('breadcrumbs.preRegisterInvoices'), disabled: true }];
      case 'pre-register-without-po':
        return [
          { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
          { label: 'Pre-Registro de Facturas sin Orden de Compra', disabled: true },
        ];
      case 'settlement':
        return [this.home(), { label: this.t('breadcrumbs.settlement'), disabled: true }];
      case 'invoice-approval':
        return [this.home(), { label: this.t('breadcrumbs.invoiceApproval'), disabled: true }];
      case 'timezone':
        return [this.home(), { label: this.t('breadcrumbs.timezoneWindowConfig'), disabled: true }];

      case 'homologation':
        return [
          { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
          { label: 'Homologación', disabled: true },
        ];

      //Beond
      case 'pacientes':
        return [this.home(), { label: this.t('breadcrumbs.patients'), disabled: true }];

      case 'pacientes/info/:id':
        return [
          this.home(),
          { label: this.t('breadcrumbs.patients'), disabled: false },
          { label: this.t('breadcrumbs.details'), disabled: true },
        ];

      case 'reservas':
        return [
          this.home(),
          { label: this.t('breadcrumbs.reservationsAndAccommodation'), disabled: true },
        ];

      case 'issuers':
        return [this.home(), { label: this.t('breadcrumbs.issuers'), disabled: true }];

      case 'facturas':
        return [this.home(), { label: this.t('breadcrumbs.invoices'), disabled: true }];

      case 'sesiones':
        return [this.home(), { label: this.t('breadcrumbs.sessionsAndTherapies'), disabled: true }];

      case 'forms':
      case 'formularios':
        return [this.home(), { label: this.t('breadcrumbs.forms'), disabled: true }];

      // Agregar más rutas según necesites...
      default:
        return this.getDefaultBreadcrumbs();
    }
  }

  // Métodos de conveniencia para establecer breadcrumbs específicos
  setMonitoringBreadcrumbs(): void {
    this.updateNavigationState({
      currentRoute: 'monitoring',
      breadcrumbPath: this.generateBreadcrumbs('monitoring'),
    });
  }

  setConnectionsBreadcrumbs(): void {
    this.updateNavigationState({
      currentRoute: 'connections',
      breadcrumbPath: this.generateBreadcrumbs('connections'),
    });
  }

  setWorkflowInfo(workflowId: number, workflowName: string): void {
    this.updateNavigationState({
      workflowId,
      workflowName,
      currentRoute: 'workflow-executions',
      breadcrumbPath: this.generateBreadcrumbs('workflow-executions'),
    });
  }

  setExecutionInfo(executionId: number, executionName: string): void {
    this.updateNavigationState({
      executionId,
      executionName,
      currentRoute: 'execution-history',
      breadcrumbPath: this.generateBreadcrumbs('execution-history'),
    });
  }

  clearNavigationState(): void {
    this.navigationState.set({});
    this.navigationSubject.next({});
  }

  getCurrentBreadcrumbs(): BreadcrumbItem[] {
    const state = this.navigationState();
    return state.breadcrumbPath || [];
  }

  private getDefaultBreadcrumbs(): BreadcrumbItem[] {
    return [this.home()];
  }

  private home(): BreadcrumbItem {
    return {
      label: this.t('breadcrumbs.dashboard'),
      routerLink: '/dashboard',
      icon: 'pi pi-home',
    };
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
